import { Link, useNavigate } from "@tanstack/react-router";
import { Fragment, useSyncExternalStore } from "react";
import { TERMS_VERSION } from "@instead/shared";
import { useT, type T } from "../lib/i18n.ts";
import { terms } from "../lib/terms.ts";
import { Ic } from "../components/Icons.tsx";
import { Markdown } from "../components/Markdown.tsx";

/**
 * The privacy notice and the terms (D-38): public, no sign-in, no tab bar —
 * plain text from the catalogs in both locales, the app's own typography.
 * Tables come from one key per table: rows split on newlines, cells on "|",
 * so a translation cannot drift out of shape.
 */
type Key = Parameters<T>[0];

function Table({ cols, rows, label }: { cols: string; rows: string; label: string }) {
  const head = cols.split("|");
  return (
    <div className="legal-scroll">
      <table className="legal-table" aria-label={label}>
        <thead><tr>{head.map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
        <tbody>
          {rows.split("\n").map((line, i) => (
            <tr key={i}>{line.split("|").map((cell, j) => j === 0 ? <th key={j} scope="row">{cell}</th> : <td key={j}>{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section({ h, body, vars }: { h: Key; body: Key; vars?: Record<string, string> }) {
  const t = useT();
  return (
    <section className="legal-section">
      <h2 className="title">{t(h)}</h2>
      <Markdown text={t(body, vars)} />
    </section>
  );
}

function LegalShell({ title, children, other }: { title: string; children: React.ReactNode; other: { to: "/privacy" | "/terms"; label: string } }) {
  const t = useT();
  const nav = useNavigate();
  const back = () => { if (window.history.length > 1) window.history.back(); else void nav({ to: "/signin" }); };
  return (
    <main className="shell-main legal">
      <div className="screen-head">
        <button type="button" className="icon-btn" aria-label={t("back")} onClick={back}>{Ic.back()}</button>
        <h1 className="title">{title}</h1>
      </div>
      <p className="micro">{t("legal.version", { date: TERMS_VERSION })}</p>
      {children}
      <p className="meta legal-foot"><Link to={other.to}>{other.label}</Link></p>
    </main>
  );
}

/** The contact address, or the visible placeholder until the forwarding address exists. */
export function contactEmail(t: T): string {
  return import.meta.env.VITE_CONTACT_EMAIL || t("legal.emailUnset");
}

export function Privacy() {
  const t = useT();
  return (
    <LegalShell title={t("privacy.title")} other={{ to: "/terms", label: t("legal.terms") }}>
      <p>{t("privacy.intro")}</p>
      <Section h="privacy.who.h" body="privacy.who.body" />
      <Section h="privacy.data.h" body="privacy.data.body" />
      <Section h="privacy.why.h" body="privacy.why.body" />
      <section className="legal-section">
        <h2 className="title">{t("privacy.recipients.h")}</h2>
        <p>{t("privacy.recipients.intro")}</p>
        <Table cols={t("privacy.recipients.cols")} rows={t("privacy.recipients.rows")} label={t("privacy.recipients.h")} />
      </section>
      <section className="legal-section">
        <h2 className="title">{t("privacy.retention.h")}</h2>
        <Table cols={t("privacy.retention.cols")} rows={t("privacy.retention.rows")} label={t("privacy.retention.h")} />
      </section>
      <Section h="privacy.device.h" body="privacy.device.body" />
      <Section h="privacy.rights.h" body="privacy.rights.body" />
      <Section h="privacy.contact.h" body="privacy.contact.body" vars={{ email: contactEmail(t) }} />
      <Section h="privacy.authority.h" body="privacy.authority.body" />
      <Section h="privacy.changes.h" body="privacy.changes.body" />
    </LegalShell>
  );
}

export function Terms() {
  const t = useT();
  return (
    <LegalShell title={t("terms.title")} other={{ to: "/privacy", label: t("legal.privacy") }}>
      <Markdown text={t("terms.body")} />
    </LegalShell>
  );
}

/** "By continuing you accept the Terms and the Privacy notice." — the two names are links. */
export function AcceptLine() {
  const t = useT();
  const parts = t("legal.acceptLine").split(/(\{terms\}|\{privacy\})/);
  return (
    <p className="micro" style={{ textAlign: "center" }}>
      {parts.map((p, i) => (
        <Fragment key={i}>
          {p === "{terms}" ? <Link to="/terms">{t("legal.terms")}</Link> : p === "{privacy}" ? <Link to="/privacy">{t("legal.privacy")}</Link> : p}
        </Fragment>
      ))}
    </p>
  );
}

/** One line, once: the version changed since this person accepted (D-38). */
export function TermsBanner() {
  const t = useT();
  const pending = useSyncExternalStore(terms.subscribe, terms.isPending);
  if (!pending) return null;
  return (
    <div className="banner banner-ok legal-banner" role="status">
      <span>
        {t("legal.updated", { date: TERMS_VERSION })}{" "}
        <Link to="/terms">{t("legal.terms")}</Link> · <Link to="/privacy">{t("legal.privacy")}</Link>
      </span>
      <button type="button" className="chip" onClick={() => void terms.accept()}>{t("legal.continue")}</button>
    </div>
  );
}
